import axios from 'axios';
import { deviceFingerprintingService } from '../services/deviceFingerprinting';

// ABP API Base URL
// In development, use empty string to leverage Vite proxy (/api -> https://localhost:44306/api)
// In production, use the actual API URL from environment variable
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// Create axios instance with ABP configuration
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Required for ABP authentication cookies
});

// Token getter function - will be set by auth context
let getAccessToken: (() => string | undefined) | null = null;

export const setTokenGetter = (getter: () => string | undefined) => {
  getAccessToken = getter;
};

// Export token getter for use in other services (e.g., EPUB.js URL generation)
export const getToken = (): string | undefined => {
  return getAccessToken?.();
};

// Request interceptor - add auth token and device fingerprint hash
apiClient.interceptors.request.use(
  async (config) => {
    // Add Bearer token
    const token = getAccessToken?.();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add device fingerprint hash for DRM session validation
    // Only if fingerprinting has been initialized (cached hash exists)
    try {
      const fingerprintHash = await deviceFingerprintingService.getFingerprintHash();
      if (fingerprintHash) {
        config.headers['X-Device-Fingerprint'] = fingerprintHash;
      }
    } catch (err) {
      // Fingerprinting not initialized yet - skip header
      // This is fine for non-DRM requests
      console.debug('Device fingerprint not available for request:', config.url);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors and extract ABP error messages
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Unauthorized - user needs to login
      // The auth provider will handle redirecting to login
      console.error('Unauthorized request - user needs to authenticate');
    }

    // Extract ABP error message if available
    // ABP returns errors in format: { error: { code, message, details, validationErrors } }
    if (error.response?.data?.error) {
      const abpError = error.response.data.error;

      // Add user-friendly error message to the error object for easy access
      error.userMessage = abpError.message || 'An error occurred';
      error.errorCode = abpError.code;
      error.errorDetails = abpError.details;

      // Log the full ABP error for debugging
      console.error('ABP Error:', {
        code: abpError.code,
        message: abpError.message,
        details: abpError.details,
        status: error.response.status,
        url: error.config?.url
      });
    }

    return Promise.reject(error);
  }
);
